import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import {
  exhaustGasCleaningSystemTypeOptions,
  FORM_FIELDS,
  yesNoOptions,
  type VesselNominationOutletContext,
} from "@features/vessel-nomination/add/constants";
import type { VesselNominationFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LANG_BASE } from "../PreArrivalQuestionnaire";

const Manifolds = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();

  const hasExhaustGasCleaningSystem = formData.watch(FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM);

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${LANG_BASE}.sections.manifolds`)}
        className="grid-cols-1 lg:grid-cols-2 xl:grid-cols-12"
      >
        <div className="xl:col-span-3">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_SIZE>
            testId="nomination-size-input"
            name={FORM_FIELDS.MANIFOLDS_SIZE}
            type="text"
            label={t(`${LANG_BASE}.fields.size`)}
            onFieldChange={updateFormData}
            maxChars={20}
            adornment="in"
          />
        </div>
        <div className="xl:col-span-3">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_NUMBER>
            testId="nomination-number-input"
            name={FORM_FIELDS.MANIFOLDS_NUMBER}
            type="number"
            label={t(`${LANG_BASE}.fields.number`)}
            onFieldChange={updateFormData}
            maxDigits={2}
            canBeginWithZero={false}
          />
        </div>
        <div className="xl:col-span-3">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_DISTANCE>
            testId="nomination-distance-input"
            name={FORM_FIELDS.MANIFOLDS_DISTANCE}
            type="number"
            label={t(`${LANG_BASE}.fields.manifolds-distance`)}
            onFieldChange={updateFormData}
            maxDigits={6}
            decimals={3}
            adornment="m"
            thousandSeparator
            canBeginWithZero={false}
          />
        </div>
        <div className="xl:col-span-3">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_HEIGHT_GRAFTING>
            testId="nomination-height-grafting-input"
            name={FORM_FIELDS.MANIFOLDS_HEIGHT_GRAFTING}
            type="number"
            label={t(`${LANG_BASE}.fields.manifolds-height-grafting`)}
            onFieldChange={updateFormData}
            maxDigits={6}
            decimals={3}
            adornment="m"
            thousandSeparator
            canBeginWithZero={false}
          />
        </div>
        <div className="xl:col-span-4">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_HEIGHT_BALLAST>
            testId="nomination-height-ballast-input"
            name={FORM_FIELDS.MANIFOLDS_HEIGHT_BALLAST}
            type="number"
            label={t(`${LANG_BASE}.fields.manifolds-height-ballast`)}
            onFieldChange={updateFormData}
            maxDigits={6}
            decimals={3}
            adornment="m"
            thousandSeparator
            canBeginWithZero={false}
          />
        </div>
        <div className="xl:col-span-4">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_HEIGHT_LOADED>
            testId="nomination-height-loaded-input"
            name={FORM_FIELDS.MANIFOLDS_HEIGHT_LOADED}
            type="number"
            label={t(`${LANG_BASE}.fields.manifolds-height-loaded`)}
            onFieldChange={updateFormData}
            maxDigits={6}
            decimals={3}
            adornment="m"
            thousandSeparator
            canBeginWithZero={false}
          />
        </div>
        <div className="xl:col-span-4">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_EXTREME_HEIGHT>
            testId="nomination-extreme-height-input"
            name={FORM_FIELDS.MANIFOLDS_EXTREME_HEIGHT}
            type="number"
            label={t(`${LANG_BASE}.fields.manifolds-extreme-height`)}
            onFieldChange={updateFormData}
            maxDigits={6}
            decimals={3}
            adornment="m"
            thousandSeparator
            canBeginWithZero={false}
          />
        </div>
        <div className="xl:col-span-4">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_BCM_STERN>
            testId="nomination-distance-bow-bcm-stern-input"
            name={FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_BCM_STERN}
            type="number"
            label={t(`${LANG_BASE}.fields.manifolds-distance-bow-bcm-stern`)}
            onFieldChange={updateFormData}
            maxDigits={6}
            decimals={3}
            adornment="m"
            thousandSeparator
            canBeginWithZero={false}
          />
        </div>
        <div className="xl:col-span-4">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_SCM_STERN>
            testId="nomination-distance-bow-scm-stern-input"
            name={FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_SCM_STERN}
            type="number"
            label={t(`${LANG_BASE}.fields.manifolds-distance-bow-scm-stern`)}
            onFieldChange={updateFormData}
            maxDigits={6}
            decimals={3}
            adornment="m"
            thousandSeparator
            canBeginWithZero={false}
          />
        </div>
        <div className="xl:col-span-4">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MANIFOLDS_DISTANCE_INBOARD>
            testId="nomination-distance-inboard-input"
            name={FORM_FIELDS.MANIFOLDS_DISTANCE_INBOARD}
            type="number"
            label={t(`${LANG_BASE}.fields.manifolds-distance-inboard`)}
            onFieldChange={updateFormData}
            maxDigits={6}
            decimals={3}
            adornment="m"
            thousandSeparator
            canBeginWithZero={false}
          />
        </div>
        <div className="col-span-full flex">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.TERMINAL_LOADING_ARM_CONNECTION>
            testId="nomination-terminal-loading-arm-connection-input"
            name={FORM_FIELDS.TERMINAL_LOADING_ARM_CONNECTION}
            type="radio"
            label={t(`${LANG_BASE}.fields.terminal-loading-arm-connection`)}
            onFieldChange={updateFormData}
            options={yesNoOptions}
            horizontalRadioButtons
          />
        </div>
        <div className="col-span-full">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM>
            testId="nomination-exhaust-gas-cleaning-system-input"
            name={FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM}
            type="radio"
            label={t(`${LANG_BASE}.fields.exhaust-gas-cleaning-system`)}
            onFieldChange={updateFormData}
            options={yesNoOptions}
            horizontalRadioButtons
          />
        </div>
        {hasExhaustGasCleaningSystem && (
          <div className="col-span-full">
            <FormItem<VesselNominationFormData, typeof FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM_TYPE>
              testId="nomination-exhaust-gas-cleaning-system-type-input"
              name={FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM_TYPE}
              type="radio"
              label={t(`${LANG_BASE}.fields.exhaust-gas-cleaning-system-type`)}
              onFieldChange={updateFormData}
              options={exhaustGasCleaningSystemTypeOptions}
              horizontalRadioButtons
            />
          </div>
        )}
        <div className="col-span-full">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.CONFIRM_INERT_GAS_SYSTEM_FITTED>
            testId="nomination-confirm-inert-gas-system-fitted-input"
            name={FORM_FIELDS.CONFIRM_INERT_GAS_SYSTEM_FITTED}
            type="radio"
            label={t(`${LANG_BASE}.fields.confirm-inert-gas-system-fitted`)}
            onFieldChange={updateFormData}
            options={yesNoOptions}
            horizontalRadioButtons
          />
        </div>
        <div className="col-span-full">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.IS_INERT_GAS_SYSTEM_OPERATIONAL>
            testId="nomination-is-inert-gas-system-operational-input"
            name={FORM_FIELDS.IS_INERT_GAS_SYSTEM_OPERATIONAL}
            type="radio"
            label={t(`${LANG_BASE}.fields.is-inert-gas-system-operational`)}
            onFieldChange={updateFormData}
            options={yesNoOptions}
            horizontalRadioButtons
          />
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default Manifolds;
